import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Flex,
  HStack,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdEdit, MdStar, MdStarBorder, MdWarningAmber } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import {
  OutlineButton,
  PrimaryButton,
  StatusAlertCard,
  StatusBadge,
} from '../../../components/ui';
import { formatDateTimeDisplay } from '../../../utils/personName';
import { formatIndianDateTime } from '../../../utils/datetime';
import { getComplianceExpiryAlerts } from '../../../utils/equipmentCompliance';
import {
  EQUIPMENT_DOC_TYPES,
  EQUIPMENT_FIELD_KEYS,
  isPlaceholderCustomDocKey,
} from '../../../data/master/equipments';
import { uploadUserEquipmentDocument } from '../../../repositories/document.repository';
import { getUserEquipmentIndentHistory } from '../../../repositories/userEquipment.repository';
import { userEquipmentFromApi } from '../../../models/userEquipment.model';
import { EquipmentEditModal } from '../equipments/fleet/EquipmentEditModal';
import { EquipmentKycStatusModal } from '../equipments/fleet/EquipmentKycStatusModal';

const PHOTO_SLOTS = [
  { key: 'photo_front', label: 'Front' },
  { key: 'photo_back', label: 'Back' },
  { key: 'photo_left', label: 'Left' },
  { key: 'photo_right', label: 'Right' },
];

const PHOTO_KEY_SET = new Set(PHOTO_SLOTS.map((s) => s.key));

function humanizeKey(key) {
  return String(key || '')
    .replace(/^c_doc_/i, 'doc ')
    .replace(/_/g, ' ')
    .trim();
}

function resolvePaperDocList(ui) {
  const docs = ui?.documents || {};
  let list = [];

  if (Array.isArray(ui?.documentTypes) && ui.documentTypes.length) {
    list = ui.documentTypes.map((d) => ({
      key: d.key,
      label: d.label || humanizeKey(d.key),
    }));
  } else {
    const fromDocs = Object.keys(docs).filter((k) => !PHOTO_KEY_SET.has(k));
    if (fromDocs.length) {
      list = fromDocs.map((key) => {
        const meta = EQUIPMENT_DOC_TYPES.find((d) => d.key === key);
        return { key, label: meta?.label || humanizeKey(key) };
      });
    } else {
      list = EQUIPMENT_DOC_TYPES.filter((d) => !PHOTO_KEY_SET.has(d.key));
    }
  }

  return list.filter(({ key }) => {
    if (PHOTO_KEY_SET.has(key)) return false;
    if (isPlaceholderCustomDocKey(key) && !docs[key]) return false;
    return true;
  });
}

function equipmentAgeYears(yom) {
  const year = Number.parseInt(String(yom || '').trim(), 10);
  if (!Number.isFinite(year) || year < 1900 || year > 2100) return null;
  return new Date().getFullYear() - year;
}

function toUiEquipment(equipment) {
  if (!equipment) return null;
  return equipment.assetId ? equipment : userEquipmentFromApi(equipment) || equipment;
}

function FieldCard({ label, value, alert = false, expired = false, alertLabel = '' }) {
  const valueColor = expired
    ? 'status.error'
    : alert
      ? 'status.warning'
      : undefined;

  const card = (
    <Box
      px={3}
      py={2}
      bg={alert ? 'bg.amberTint' : 'bg.input'}
      borderRadius="sm"
      borderWidth="1px"
      borderColor={alert ? 'utkarsh.amber' : 'border.subtle'}
      w="100%"
      cursor={alert ? 'help' : undefined}
      transition="border-color 0.15s ease, box-shadow 0.15s ease"
      _hover={
        alert
          ? {
              borderColor: expired ? 'status.error' : 'brand.600',
              boxShadow: 'sm',
            }
          : undefined
      }
    >
      <Text fontSize="11px" color="text.secondary" mb={0.5} fontWeight="500">
        {label}
      </Text>
      <Text
        fontSize="14px"
        fontWeight="600"
        whiteSpace="pre-wrap"
        color={valueColor}
      >
        {value || '—'}
      </Text>
    </Box>
  );

  if (!alert || !alertLabel) return card;

  return (
    <Tooltip
      label={alertLabel}
      hasArrow
      placement="top"
      openDelay={200}
      bg={expired ? 'status.error' : 'utkarsh.ink'}
      color="white"
      fontSize="13px"
      px={3}
      py={2}
      borderRadius="md"
    >
      {card}
    </Tooltip>
  );
}

function materialStatusLabel(status) {
  if (status === 'verified') return 'Verified';
  if (status === 'rejected') return 'Rejected';
  return 'Under review';
}

function statusBadgeProps(status) {
  if (status === 'Verified') return { status: 'verified' };
  if (status === 'Rejected') return { status: 'error' };
  return { status: 'pending' };
}

function formatDateTime(value) {
  return formatDateTimeDisplay(value);
}

function StarRatingDisplay({ value }) {
  const rating = Number(value);
  if (!Number.isFinite(rating) || rating < 1) {
    return (
      <Text as="span" color="text.secondary">
        —
      </Text>
    );
  }
  const filled = Math.min(5, Math.max(0, Math.round(rating)));
  return (
    <HStack spacing={0.5} aria-label={`${filled} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Icon
          key={n}
          as={n <= filled ? MdStar : MdStarBorder}
          boxSize="16px"
          color={n <= filled ? 'brand.500' : 'gray.300'}
        />
      ))}
    </HStack>
  );
}

/**
 * equipment: raw API shape from member detail (or UI-mapped).
 */
export function MemberEquipmentDetailModal({
  isOpen,
  onClose,
  equipment,
  onUpdated,
  onViewImage,
}) {
  const navigate = useNavigate();
  const kycModal = useDisclosure();
  const editModal = useDisclosure();
  const toast = useToast();
  const fileRef = useRef(null);
  const pendingDocKeyRef = useRef(null);
  const [busyKey, setBusyKey] = useState('');
  const [docOverrides, setDocOverrides] = useState({});
  const [localAsset, setLocalAsset] = useState(null);
  const [indentHistory, setIndentHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const ui = useMemo(
    () => toUiEquipment(localAsset || equipment),
    [localAsset, equipment],
  );

  useEffect(() => {
    if (isOpen) {
      setDocOverrides({});
      setLocalAsset(null);
    }
  }, [isOpen, equipment]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const id = equipment?.id || equipment?.userEquipmentId;
    if (!id) {
      setIndentHistory([]);
      return undefined;
    }
    let cancelled = false;
    setHistoryLoading(true);
    getUserEquipmentIndentHistory(id)
      .then((rows) => {
        if (!cancelled) setIndentHistory(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setIndentHistory([]);
          toast({
            title: err?.message || 'Failed to load work history',
            status: 'error',
            position: 'top-right',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, equipment, toast]);

  const documents = useMemo(
    () => ({ ...(ui?.documents || {}), ...docOverrides }),
    [ui, docOverrides],
  );

  const view = useMemo(
    () => (ui ? { ...ui, documents } : null),
    [ui, documents],
  );

  const reviewLabel = materialStatusLabel(view?.kycStatus || view?.status);
  const ageYears = equipmentAgeYears(view?.yom);
  const paperDocs = useMemo(() => resolvePaperDocList(view), [view]);
  const complianceAlerts = useMemo(
    () => getComplianceExpiryAlerts(view),
    [view],
  );

  const pickFile = (docKey) => {
    pendingDocKeyRef.current = docKey;
    if (fileRef.current) {
      fileRef.current.accept = 'image/*,application/pdf';
      fileRef.current.click();
    }
  };

  const handleFileChosen = async (e) => {
    const file = e.target.files?.[0];
    const docType = pendingDocKeyRef.current;
    e.target.value = '';
    pendingDocKeyRef.current = null;
    if (!file || !docType || !view?.id) return;

    setBusyKey(docType);
    try {
      await uploadUserEquipmentDocument({
        equipmentId: view.id,
        docType,
        file,
      });
      toast({
        title: 'Document saved',
        status: 'success',
        position: 'top-right',
      });
      setDocOverrides((prev) => ({
        ...prev,
        [docType]: URL.createObjectURL(file),
      }));
      onUpdated?.();
    } catch (err) {
      toast({
        title: err?.message || 'Upload failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusyKey('');
    }
  };

  if (!view) return null;

  return (
    <>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={handleFileChosen}
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="xl"
        isCentered
        scrollBehavior="inside"
        blockScrollOnMount
      >
        <ModalOverlay bg="blackAlpha.600" />
        <ModalContent
          bg="bg.surface"
          borderRadius="md"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
          overflow="hidden"
        >
          <ModalHeader pb={2} flexShrink={0}>
            Equipment details
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            flex="1 1 auto"
            overflowY="auto"
            minH={0}
            maxH="calc(90vh - 8.5rem)"
            pb={4}
            sx={{ overscrollBehavior: 'contain' }}
          >
            <VStack align="stretch" spacing={3}>
              <Flex gap={2} wrap="wrap" align="center">
                <StatusBadge {...statusBadgeProps(reviewLabel)}>{reviewLabel}</StatusBadge>
                <Text fontSize="13px" color="text.secondary">
                  {view.equipmentType || view.equipmentTypeName} · {view.assetId || view.regNo}
                </Text>
              </Flex>

              {view.rejectionReason ? (
                <Box
                  p={3}
                  bg="red.50"
                  borderRadius="sm"
                  borderWidth="1px"
                  borderColor="red.200"
                >
                  <Text fontSize="12px" fontWeight="600" color="red.700">
                    Rejection reason
                  </Text>
                  <Text fontSize="14px" mt={1}>
                    {view.rejectionReason}
                  </Text>
                </Box>
              ) : null}

              {ageYears != null && ageYears >= 5 ? (
                <StatusAlertCard
                  variant="pending"
                  icon={MdWarningAmber}
                  title="Equipment older than 5 years"
                  body={`Year of manufacture is ${view.yom} (${ageYears} years old). Review related documents carefully.`}
                />
              ) : null}

              <Box>
                <Text fontSize="13px" fontWeight="700" mb={2}>
                  Side photos
                </Text>
                <SimpleGrid columns={4} spacing={2}>
                  {PHOTO_SLOTS.map(({ key, label }) => {
                    const url = documents[key];
                    const busy = busyKey === key;
                    return (
                      <Box
                        key={key}
                        overflow="hidden"
                        bg="bg.input"
                        borderRadius="sm"
                        borderWidth="1px"
                        borderColor="border.subtle"
                      >
                        <Box
                          w="100%"
                          h="64px"
                          bg="blackAlpha.50"
                          overflow="hidden"
                          cursor={url ? 'pointer' : 'default'}
                          onClick={() => url && onViewImage?.(url, `Photo — ${label}`)}
                        >
                          {url ? (
                            <Image src={url} alt={label} w="100%" h="100%" objectFit="cover" />
                          ) : (
                            <Flex w="100%" h="100%" align="center" justify="center">
                              <Text fontSize="10px" color="text.secondary">
                                —
                              </Text>
                            </Flex>
                          )}
                        </Box>
                        <Flex direction="column" gap={1} px={1.5} py={1.5}>
                          <Text fontSize="11px" fontWeight="600">
                            {label}
                          </Text>
                          <OutlineButton size="xs" isLoading={busy} onClick={() => pickFile(key)}>
                            {url ? 'Update' : 'Upload'}
                          </OutlineButton>
                        </Flex>
                      </Box>
                    );
                  })}
                </SimpleGrid>
              </Box>

              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={2}>
                <FieldCard label="Type" value={view.equipmentType || view.equipmentTypeName} />
                <FieldCard label="Registration number" value={view.regNo} />
                <FieldCard label="Category" value={view.equipmentTypeCategory} />
                <FieldCard label="Vehicle class" value={view.vehicleClass} />
                {EQUIPMENT_FIELD_KEYS.filter(
                  ({ key }) =>
                    key !== 'regNo' &&
                    view.fieldRequirements?.[key] !== 'hidden'
                ).map(({ key, label }) => {
                  const alert = complianceAlerts.find((a) => a.key === key);
                  return (
                    <FieldCard
                      key={key}
                      label={label}
                      value={view[key]}
                      alert={Boolean(alert)}
                      expired={Boolean(alert?.isExpired)}
                      alertLabel={alert?.shortMessage || alert?.message || ''}
                    />
                  );
                })}
                <FieldCard label="Created" value={formatDateTime(view.createdAt)} />
                <FieldCard label="Updated" value={formatDateTime(view.updatedAt)} />
              </SimpleGrid>

              <Box>
                <Text fontSize="13px" fontWeight="700" mb={2}>
                  Documents
                </Text>
                <VStack align="stretch" spacing={1.5}>
                  {paperDocs.map(({ key, label }) => {
                    const url = documents[key];
                    const busy = busyKey === key;
                    return (
                      <Flex
                        key={key}
                        justify="space-between"
                        align="center"
                        gap={2}
                        px={2}
                        py={1.5}
                        bg="bg.input"
                        borderRadius="sm"
                      >
                        <Box minW={0} flex="1">
                          <Text fontSize="13px" fontWeight="600" noOfLines={1}>
                            {label}
                          </Text>
                          <Text fontSize="11px" color="text.secondary">
                            {url ? 'Uploaded' : 'Not uploaded'}
                          </Text>
                        </Box>
                        <HStack spacing={1} flexShrink={0}>
                          {url ? (
                            <OutlineButton
                              size="xs"
                              onClick={() => onViewImage?.(url, label)}
                              isDisabled={busy}
                            >
                              Open
                            </OutlineButton>
                          ) : null}
                          <OutlineButton size="xs" isLoading={busy} onClick={() => pickFile(key)}>
                            {url ? 'Update' : 'Upload'}
                          </OutlineButton>
                        </HStack>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
              <Box>
                <Text fontSize="13px" fontWeight="700" mb={2}>
                  Work history & ratings
                </Text>
                {historyLoading ? (
                  <Flex justify="center" py={4}>
                    <Spinner size="sm" />
                  </Flex>
                ) : (
                  <Box
                    borderWidth="1px"
                    borderColor="border.subtle"
                    borderRadius="md"
                    overflowX="auto"
                  >
                    <Table size="sm">
                      <Thead>
                        <Tr>
                          <Th>Event</Th>
                          <Th>Request</Th>
                          <Th>When</Th>
                          <Th>Rating</Th>
                          <Th>Note</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {indentHistory.map((h, idx) => (
                          <Tr
                            key={`${h.indentId}-${h.kind}-${idx}`}
                            cursor="pointer"
                            _hover={{ bg: 'bg.input' }}
                            onClick={() =>
                              navigate(`/admin/equipments/indents/${h.indentId}`)
                            }
                          >
                            <Td>
                              <StatusBadge
                                status={
                                  h.kind === 'Deployed'
                                    ? 'warning'
                                    : h.kind === 'Released'
                                      ? 'verified'
                                      : 'info'
                                }
                              >
                                {h.kind}
                              </StatusBadge>
                            </Td>
                            <Td fontFamily="mono">{h.indentId}</Td>
                            <Td fontSize="12px">
                              {h.at
                                ? formatIndianDateTime(h.at) || '—'
                                : '—'}
                            </Td>
                            <Td>
                              <StarRatingDisplay value={h.rating} />
                            </Td>
                            <Td fontSize="12px" color="text.secondary">
                              {h.note || '—'}
                            </Td>
                          </Tr>
                        ))}
                        {indentHistory.length === 0 ? (
                          <Tr>
                            <Td colSpan={5}>
                              <Text
                                color="text.secondary"
                                py={4}
                                textAlign="center"
                              >
                                No recommendations or deployments yet.
                              </Text>
                            </Td>
                          </Tr>
                        ) : null}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter
            gap={2}
            flexShrink={0}
            borderTopWidth="1px"
            borderColor="border.subtle"
          >
            <OutlineButton leftIcon={<MdEdit />} onClick={editModal.onOpen}>
              Edit details
            </OutlineButton>
            <PrimaryButton onClick={kycModal.onOpen}>Update status</PrimaryButton>
            <OutlineButton onClick={onClose}>Close</OutlineButton>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <EquipmentEditModal
        isOpen={editModal.isOpen}
        onClose={editModal.onClose}
        asset={view}
        onSaved={(updated) => {
          if (updated) setLocalAsset(updated);
          onUpdated?.();
        }}
      />

      <EquipmentKycStatusModal
        isOpen={kycModal.isOpen}
        onClose={kycModal.onClose}
        asset={view}
        useMemberEquipment
        onSaved={() => {
          onUpdated?.();
          onClose();
        }}
      />
    </>
  );
}
